import {
  ArrowDown,
  AtSign,
  CircleHelp,
  SlidersHorizontal,
  Inbox,
  Clock,
  Star,
  Megaphone,
  RadioTower,
  Hand,
  DoorOpen,
  Accessibility,
  Headphones,
  Pause,
  BellPlus,
  BellOff,
  ClipboardCheck,
  MessageSquareText,
  ShieldAlert,
  ShieldCheck,
  Crown,
  Eye,
  EyeOff,
  Webhook,
  MessageCircle,
  ChevronLeft,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  Play,
  Maximize2,
  Minimize2,
  LayoutGrid,
  PictureInPicture2,
  ScreenShareOff,
  Phone,
  Folder,
  CalendarDays,
  MapPin,
  ArrowLeft,
  ArrowUp,
  ArrowRightToLine,
  AudioLines,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  CornerDownLeft,
  Ellipsis,
  Forward,
  Timer,
  CornerUpLeft,
  Hash,
  Infinity as InfinityIcon,
  Lock,
  Mail,
  Menu,
  MessagesSquare,
  Mic,
  MicOff,
  Minus,
  MonitorUp,
  Pencil,
  Pin,
  PhoneOff,
  Plus,
  Reply,
  Search,
  Settings,
  SignalHigh,
  SignalLow,
  SignalMedium,
  SignalZero,
  Smile,
  Square,
  TriangleAlert,
  Users,
  Video,
  Volume2,
  VolumeX,
  X,
  type LucideIcon,
  type LucideProps,
  Bell,
  KeyRound,
  LogOut,
  MonitorSmartphone,
  ScrollText,
  SunMoon,
  User,
  Camera,
  Copy,
  Gamepad2,
  Link,
  Monitor,
  Bot,
  Keyboard,
  Music,
  Palette,
  Quote,
  Ban,
  IdCard,
  ShieldUser,
  Sticker,
  ImagePlay,
  Leaf,
  Soup,
  Bike,
  Lightbulb,
  Heart,
  Tag,
  Trash,
  UserPlus,
  UserCheck,
  Hourglass,
  CirclePlus,
  FolderPlus,
  CalendarPlus,
  Shield,
  SquareSlash,
  Bookmark,
  MessageSquareDot,
} from "lucide-react";

/*
 * Значки интерфейса — Lucide, настроенный под систему.
 *
 * Через этот модуль, а не из `lucide-react` напрямую: у Lucide по умолчанию
 * 24 пикселя и толщина линии 2, и то и другое рассчитано на просторный
 * интерфейс. Наш плотный (0.70×) и набран лёгким начертанием — значок в его
 * умолчаниях выглядел бы рядом с подписью жирным пятном.
 *
 * Настройка одна на все места ровно по той же причине, по какой цвета берутся
 * из одного моста: разойдясь по полусотне вызовов, размер и толщина обратно
 * уже не собираются.
 *
 * Значения перебиваются на месте, где это оправдано: `<X size={12} />`.
 *
 * NB: `theme.json` в проекте Nocturne держит `iconSet: "phosphor"`. Набор
 * сменён по решению владельца продукта, и запись в проекте дизайна с этим
 * теперь расходится — её стоит поправить там же.
 */
function tuned(Base: LucideIcon, name: string) {
  function Tuned({ size = 15, strokeWidth = 1.75, ...rest }: LucideProps) {
    return <Base size={size} strokeWidth={strokeWidth} {...rest} />;
  }

  Tuned.displayName = name;
  return Tuned;
}

/** Закрыть: окно, панель, чип. */
export const IconClose = tuned(X, "IconClose");
/** Выбрано — галочка в меню и в списке. */
export const IconCheck = tuned(Check, "IconCheck");
/** Раскрыть вниз: поле выбора, заголовок с меню. */
export const IconChevronDown = tuned(ChevronDown, "IconChevronDown");
/** Свёрнуто — категория каналов. */
export const IconChevronRight = tuned(ChevronRight, "IconChevronRight");
export const IconChevronLeft = tuned(ChevronLeft, "IconChevronLeft");
/** Просмотрщик картинок и видео. */
export const IconExternal = tuned(ExternalLink, "IconExternal");
export const IconZoomIn = tuned(ZoomIn, "IconZoomIn");
export const IconZoomOut = tuned(ZoomOut, "IconZoomOut");
export const IconPlay = tuned(Play, "IconPlay");
export const IconPause = tuned(Pause, "IconPause");
export const IconExpand = tuned(Maximize2, "IconExpand");
export const IconShrink = tuned(Minimize2, "IconShrink");
/** Звонок: вернуться к сетке, картинка в картинке, остановить свой показ. */
export const IconGrid = tuned(LayoutGrid, "IconGrid");
export const IconPip = tuned(PictureInPicture2, "IconPip");
export const IconShareOff = tuned(ScreenShareOff, "IconShareOff");
/** Папка серверов в ряду. */
export const IconFolder = tuned(Folder, "IconFolder");
/** Звонок в личной переписке. */
export const IconCall = tuned(Phone, "IconCall");
/** Поле списка выбора: значок набора v6 — вверх-вниз. */
export const IconChevronsUpDown = tuned(ChevronsUpDown, "IconChevronsUpDown");
/** Назад по экрану. */
export const IconBack = tuned(ArrowLeft, "IconBack");
/** Вниз — к новым сообщениям. */
export const IconDown = tuned(ArrowDown, "IconDown");
/** Меню на узком экране. */
export const IconMenu = tuned(Menu, "IconMenu");
/** Настройки. */
export const IconSettings = tuned(Settings, "IconSettings");
/** Изменить своё сообщение. */
export const IconEdit = tuned(Pencil, "IconEdit");
/** Ответить в ленте. */
export const IconReply = tuned(Reply, "IconReply");
/** На что отвечают — над цитатой. */
export const IconQuoted = tuned(CornerUpLeft, "IconQuoted");
/** Ветки канала. */
export const IconThreads = tuned(MessagesSquare, "IconThreads");
/** Закреплённые сообщения канала. */
export const IconPinned = tuned(Pin, "IconPinned");
/** Список участников сервера. */
export const IconMembers = tuned(Users, "IconMembers");
/** Чат голосового канала — пузырь, как в Discord. */
export const IconChat = tuned(MessageCircle, "IconChat");
/** Вебхуки канала. */
export const IconWebhook = tuned(Webhook, "IconWebhook");
/** Спойлер у вложения: открыто и закрыто. */
export const IconEye = tuned(Eye, "IconEye");
export const IconEyeOff = tuned(EyeOff, "IconEyeOff");
/** Владелец группы — корона, как в Discord. */
export const IconOwner = tuned(Crown, "IconOwner");
/** Выбрать знак. */
export const IconEmoji = tuned(Smile, "IconEmoji");
/** Поиск. */
export const IconSearch = tuned(Search, "IconSearch");
export const IconStar = tuned(Star, "IconStar");
export const IconRecent = tuned(Clock, "IconRecent");
/** Личные переписки. */
export const IconDirects = tuned(Mail, "IconDirects");
/** Текстовый канал. */
export const IconTextChannel = tuned(Hash, "IconTextChannel");
/** Голосовой канал. */
export const IconVoiceChannel = tuned(Volume2, "IconVoiceChannel");

export const IconEvents = tuned(CalendarDays, "IconEvents");

export const IconPlace = tuned(MapPin, "IconPlace");

export const IconSilent = tuned(BellOff, "IconSilent");

export const IconAutoMod = tuned(ShieldAlert, "IconAutoMod");

export const IconForumChannel = tuned(MessageSquareText, "IconForumChannel");
/** Канал объявлений. */
export const IconAnnouncementChannel = tuned(Megaphone, "IconAnnouncementChannel");
/** Сцена — вышка, как значок stage-канала в Discord. */
export const IconStageChannel = tuned(RadioTower, "IconStageChannel");
/** Поднятая рука на сцене — «прошу слова». */
export const IconHand = tuned(Hand, "IconHand");
/** «Специальные возможности» в настройках. */
export const IconAccessibility = tuned(Accessibility, "IconAccessibility");
/** Экран приветствия сервера — открытая дверь. */
export const IconWelcome = tuned(DoorOpen, "IconWelcome");
/** «Стать слушателем» на сцене. */
export const IconListen = tuned(Headphones, "IconListen");
/** Подписаться на канал объявлений. */
export const IconFollow = tuned(BellPlus, "IconFollow");

export const IconRules = tuned(ClipboardCheck, "IconRules");
/** Безопасность сервера: проверка новичков и пауза приглашений. */
export const IconSafety = tuned(ShieldCheck, "IconSafety");
/** Закрытый канал — замок вместо решётки. */
export const IconPrivateChannel = tuned(Lock, "IconPrivateChannel");
/** Микрофон включён. */
export const IconMic = tuned(Mic, "IconMic");
/** Микрофон выключен. */
export const IconMicOff = tuned(MicOff, "IconMicOff");
/** Звук включён. */
export const IconSound = tuned(Volume2, "IconSound");
/** Звук выключен — оглох. */
export const IconSoundOff = tuned(VolumeX, "IconSoundOff");
/** Камера. */
export const IconCamera = tuned(Video, "IconCamera");
/** Показ экрана. */
export const IconShare = tuned(MonitorUp, "IconShare");
/** Выйти из звонка. */
export const IconLeaveCall = tuned(PhoneOff, "IconLeaveCall");
/*
 * Как держится связь — четырьмя ступенями.
 *
 * Ступенями, а не одним значком разного цвета: цвет читают не все, а высота
 * столбиков видна и в оттенках серого. Цвет к ним всё равно добавляется —
 * два признака на одно и то же надёжнее одного.
 */
export const IconLinkHigh = tuned(SignalHigh, "IconLinkHigh");
export const IconLinkMedium = tuned(SignalMedium, "IconLinkMedium");
export const IconLinkLow = tuned(SignalLow, "IconLinkLow");
export const IconLinkNone = tuned(SignalZero, "IconLinkNone");
/** Звуковая панель: набор коротких звуков в звонок. */
export const IconSoundboard = tuned(AudioLines, "IconSoundboard");
/** Свернуть окно (полоса окна в сборке Tauri). */
export const IconMinimize = tuned(Minus, "IconMinimize");
/** Развернуть окно. */
export const IconMaximize = tuned(Square, "IconMaximize");
/** Без ограничения — у приглашения. */
export const IconEndless = tuned(InfinityIcon, "IconEndless");
/** Завести ещё одно — в шапке столбца ролей. */
export const IconPlus = tuned(Plus, "IconPlus");
/** Отправить: стрелка вверх в капле справа от поля ввода. */
export const IconSend = tuned(ArrowUp, "IconSend");
/* Разделы окна настроек — значками из макета v6. */
export const IconUser = tuned(User, "IconUser");
export const IconKey = tuned(KeyRound, "IconKey");
export const IconBot = tuned(Bot, "IconBot");
export const IconCommand = tuned(SquareSlash, "IconCommand");
export const IconBell = tuned(Bell, "IconBell");
/** «Входящие» — лоток, как в Discord: не конверт, тот у личных сообщений по привычке. */
export const IconInbox = tuned(Inbox, "IconInbox");
export const IconHelp = tuned(CircleHelp, "IconHelp");
export const IconAt = tuned(AtSign, "IconAt");
export const IconFilters = tuned(SlidersHorizontal, "IconFilters");
/** Тайм-аут участника — у имени в списке. */
export const IconTimer = tuned(Timer, "IconTimer");
/** Переслать и «Переслано» над снимком. */
export const IconForward = tuned(Forward, "IconForward");
/** «Ещё» — меню действий, как ⋯ в ряду над сообщением в Discord. */
export const IconMore = tuned(Ellipsis, "IconMore");
export const IconDevices = tuned(MonitorSmartphone, "IconDevices");
export const IconTheme = tuned(SunMoon, "IconTheme");
export const IconLog = tuned(ScrollText, "IconLog");
export const IconLogOut = tuned(LogOut, "IconLogOut");
/* Карточка профиля и «Доска» в настройках — тоже из макета. */
export const IconPhoto = tuned(Camera, "IconPhoto");
export const IconPalette = tuned(Palette, "IconPalette");
export const IconCopy = tuned(Copy, "IconCopy");
export const IconGame = tuned(Gamepad2, "IconGame");
export const IconMusic = tuned(Music, "IconMusic");
export const IconMonitor = tuned(Monitor, "IconMonitor");
export const IconKeyboard = tuned(Keyboard, "IconKeyboard");
export const IconQuote = tuned(Quote, "IconQuote");
export const IconLink = tuned(Link, "IconLink");
/* Разделы настроек сервера и канала. */
export const IconIdCard = tuned(IdCard, "IconIdCard");
export const IconTag = tuned(Tag, "IconTag");
export const IconSticker = tuned(Sticker, "IconSticker");
/* Гифки у поля ввода — картинка с «играть»: своего знака GIF в Lucide нет. */
export const IconImagePlay = tuned(ImagePlay, "IconImagePlay");
/* Разделы обычных эмодзи в колонке панели — как в Discord. */
export const IconLeaf = tuned(Leaf, "IconLeaf");
export const IconSoup = tuned(Soup, "IconSoup");
export const IconBike = tuned(Bike, "IconBike");
export const IconLightbulb = tuned(Lightbulb, "IconLightbulb");
export const IconHeart = tuned(Heart, "IconHeart");
/* Эмодзи чужого сервера: виден, но здесь его не поставить. */
export const IconLock = tuned(Lock, "IconLock");
export const IconRoles = tuned(ShieldUser, "IconRoles");
export const IconInvite = tuned(UserPlus, "IconInvite");
/* Дружба в профиле, как в Discord: друг — с галочкой, заявка ушла — песочные часы. */
export const IconFriend = tuned(UserCheck, "IconFriend");
export const IconFriendPending = tuned(Hourglass, "IconFriendPending");
export const IconBan = tuned(Ban, "IconBan");
export const IconTrash = tuned(Trash, "IconTrash");
/** Не задано — среднее из трёх положений права. */
export const IconUnset = tuned(Minus, "IconUnset");
/** Настройка может неожиданно ухудшить результат. */
export const IconWarning = tuned(TriangleAlert, "IconWarning");
/** Клавиша ввода — в подсказке под списком знаков. */
export const IconEnter = tuned(CornerDownLeft, "IconEnter");
/** Клавиша перехода — там же. */
export const IconTab = tuned(ArrowRightToLine, "IconTab");
/** Меню сервера: создать канал, категорию, событие — как в Discord. */
export const IconAddChannel = tuned(CirclePlus, "IconAddChannel");
export const IconAddCategory = tuned(FolderPlus, "IconAddCategory");
export const IconAddEvent = tuned(CalendarPlus, "IconAddEvent");
/** Настройки конфиденциальности. */
export const IconPrivacy = tuned(Shield, "IconPrivacy");
/* Ряд над сообщением с Shift: закладка и «Отметить непрочитанным», как в Discord. */
export const IconBookmark = tuned(Bookmark, "IconBookmark");
export const IconMarkUnread = tuned(MessageSquareDot, "IconMarkUnread");
